// ============================================================
// Puppies.js — premium public listing for Puppies + Litters
// ============================================================
//
// What you get:
// - Keeps your Helmet + hero section exactly as you had it
// - Filters: All | Puppies | Litters
// - Search (name, description, parents, color, status)
// - Sort (Featured/Order, Newest (DOB/Expected), Price, Name)
// - Client-side pagination with "Load more"
// - Distinct cards for Puppy vs Litter
// - Reuses `color` field for litters as "Expected Color"
// - Status chips, parents, optional price
// - Skeletons, empty states, reveal animations
// - JSON-LD structured data for ItemList + Offer
// - A11y labels and keyboard focus management
//
// Notes:
// - Path to puppiesAPI matches your current import.
// - If you change the route path for this page, update the JSON-LD URLs below.
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { listPublicPuppies } from "../utils/puppiesAPI";
import "../styles/Puppies.css"; // keeps your current stylesheet structure :contentReference[oaicite:1]{index=1}

// ---------------------------------------------
// Utilities
// ---------------------------------------------

const money = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? `$${n.toLocaleString()}`
    : null;

function safeDate(value) {
  if (!value) return "";
  try {
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function useIntersection() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

function toItemListJsonLd(url, items) {
  // Minimal ItemList schema to help SERPs understand ordering
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    url,
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${url}#${item.id || `entry-${i}`}`,
      name: item.name || "Unnamed",
    })),
  };
}

function toPuppyOfferJsonLd(baseUrl, item) {
  // Only emit Offer when a valid price exists
  if (typeof item.price !== "number" || Number.isNaN(item.price)) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.name || "Golden Retriever Puppy",
    image: item.imageUrl || undefined,
    description: item.description || undefined,
    brand: "DeClerck’s Goldens",
    url: `${baseUrl}#${item.id}`,
    offers: {
      "@type": "Offer",
      price: item.price,
      priceCurrency: "USD",
      availability:
        String(item.status || "").toLowerCase() === "reserved"
          ? "https://schema.org/PreOrder"
          : "https://schema.org/InStock",
      url: `${baseUrl}#${item.id}`,
    },
  };
}

// ---------------------------------------------
// Page
// ---------------------------------------------

export default function PuppiesPage() {
  // Core data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // UI state
  const [view, setView] = useState("all"); // all | puppy | litter
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("order"); // order | newest | price | name
  const [page, setPage] = useState(0); // pagination page index
  const PAGE_SIZE = 12;

  // Focus management for accessibility
  const gridRef = useRef(null);

  // -------------------------------------------
  // Load
  // -------------------------------------------
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await listPublicPuppies();
        if (!active) return;
        // normalize legacy docs to stable shape
        const normalized = (data || []).map((r) => ({
          id: r.id,
          entryType: r.entryType || "puppy",
          public: r.public !== false,
          name: r.name || "Unnamed",
          description: r.description || "",
          imageUrl: r.imageUrl || "",
          order: typeof r.order === "number" ? r.order : 9999,
          status: r.status || "Available", // puppy only
          price: typeof r.price === "number" ? r.price : null, // puppy only
          sex: r.sex || "", // puppy only
          color: r.color || "",
          dob: r.dob || "", // puppy only
          availableDate: r.availableDate || "", // puppy only
          damName: r.damName || "",
          studName: r.studName || "",
          expectedDate: r.expectedDate || "", // litter only
        }));
        setRows(normalized);
      } catch (e) {
        console.error(e);
        if (active)
          setErr(
            "We couldn’t load the listings right now. Please try again soon."
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Reset paging when filters change
  useEffect(() => {
    setPage(0);
  }, [view, q, sort]);

  // -------------------------------------------
  // Derived data
  // -------------------------------------------
  const filtered = useMemo(() => {
    let items = rows.filter((r) => r.public !== false);

    if (view !== "all") {
      items = items.filter((r) => r.entryType === view);
    }

    const term = q.trim().toLowerCase();
    if (term) {
      items = items.filter((r) =>
        [
          r.name,
          r.description,
          r.sex,
          r.color,
          r.damName,
          r.studName,
          r.status,
          r.entryType,
        ]
          .filter(Boolean)
          .some((t) => String(t).toLowerCase().includes(term))
      );
    }

    switch (sort) {
      case "newest":
        items = [...items].sort((a, b) => {
          const aKey = a.entryType === "puppy" ? a.dob : a.expectedDate;
          const bKey = b.entryType === "puppy" ? b.dob : b.expectedDate;
          if (aKey && bKey) return String(bKey).localeCompare(String(aKey));
          if (aKey) return -1;
          if (bKey) return 1;
          return (a.order ?? 9999) - (b.order ?? 9999);
        });
        break;
      case "price":
        items = [...items].sort((a, b) => {
          const ap = typeof a.price === "number" ? a.price : Infinity;
          const bp = typeof b.price === "number" ? b.price : Infinity;
          return ap - bp;
        });
        break;
      case "name":
        items = [...items].sort((a, b) =>
          String(a.name).localeCompare(String(b.name))
        );
        break;
      default:
        items = [...items].sort(
          (a, b) => (a.order ?? 9999) - (b.order ?? 9999)
        );
    }

    return items;
  }, [rows, view, q, sort]);

  // Pagination slices
  const pages = useMemo(() => chunk(filtered, PAGE_SIZE), [filtered]);
  const visible = pages[page] || [];

  // For JSON-LD
  const currentUrl =
    typeof window !== "undefined"
      ? window.location.origin + "/puppies"
      : "https://www.declercksgoldens.com/puppies";
  const itemListJson = useMemo(
    () => toItemListJsonLd(currentUrl, filtered),
    [currentUrl, filtered]
  );
  const offerJsonList = useMemo(
    () =>
      filtered
        .map((i) => toPuppyOfferJsonLd(currentUrl, i))
        .filter(Boolean),
    [currentUrl, filtered]
  );

  // -------------------------------------------
  // Render
  // -------------------------------------------
  return (
    <div className="puppies-page">
      {/* ================= SEO kept from your original ================= */}
      <Helmet>
        <title>Golden Retriever Puppies for Adoption | DeClerck’s Goldens</title>
        <meta
          name="description"
          content="Browse available Golden Retriever puppies and upcoming litters at DeClerck’s Goldens. Family-raised, AKC registered, and health-focused."
        />
        <meta property="og:title" content="Golden Retriever Puppies for Adoption | DeClerck’s Goldens" />
        <meta
          property="og:description"
          content="See our current Golden Retriever puppies and upcoming litters raised with love and care."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={`${currentUrl}/preview-puppies.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Golden Retriever Puppies for Adoption | DeClerck’s Goldens" />
        <meta
          name="twitter:description"
          content="Meet our Golden Retriever puppies — healthy, family raised, and ready for adoption."
        />
        <meta name="twitter:image" content={`${currentUrl}/preview-puppies.jpg`} />
        <link rel="canonical" href={currentUrl} />

        {/* JSON-LD: ItemList plus per-item Offers for priced puppies */}
        <script type="application/ld+json">
          {JSON.stringify(itemListJson)}
        </script>
        {offerJsonList.map((obj, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
      </Helmet>

      {/* ================= HERO kept & untouched structure ================= */}
      <section className="puppies-hero" aria-labelledby="hero-title">
        <div className="puppies-hero-bg" aria-hidden="true">
          <span className="orb orb-a"></span>
          <span className="orb orb-b"></span>
          <span className="orb orb-c"></span>
        </div>
        <div className="puppies-hero-inner">
          <h1 id="hero-title" className="puppies-hero-title">
            Golden Retriever Puppies raised for health and heart
          </h1>
          <p className="puppies-hero-text">
            For more than thirty years our family has raised goldens with sound genetics,
            wonderful temperaments, and a love for people. Puppies grow up in our home, are
            handled daily, and leave with a confident start for yours.
          </p>
          <div className="puppies-hero-ctas" role="group" aria-label="Primary actions">
            <Link to="/contact" className="puppy-cta-big">
              Contact Us
            </Link>
            <a href="#available" className="puppy-ghost-cta">
              See Puppies ↓
            </a>
          </div>
          <ul className="puppies-hero-chips" aria-label="Program highlights">
            <li>👨‍👩‍👧‍👦 Family Raised</li>
            <li>🩺 Health Focused</li>
            <li>📜 AKC Registered</li>
            <li>🤝 Lifetime Support</li>
          </ul>
          <div className="puppies-hero-note">
            Puppies cannot be held without a deposit. Reach out and we will guide you through the next steps.
          </div>
        </div>
      </section>

      {/* ================= HEADER ================= */}
      <section id="available" className="puppies-header" aria-labelledby="available-heading">
        <h2 id="available-heading" className="puppies-title">
          Available Golden Retriever Puppies
        </h2>
        <p className="puppies-subtitle">
          We keep this page updated so you can see who is ready for visits or will be ready soon.
        </p>
      </section>

      {/* ================= Controls ================= */}
      <ControlsBar
        view={view}
        setView={setView}
        q={q}
        setQ={setQ}
        sort={sort}
        setSort={setSort}
        resultsCount={filtered.length}
      />

      {/* ================= States ================= */}
      {loading && <LoaderSkeleton count={8} />}
      {!loading && err && <div role="alert" className="puppies-error">{err}</div>}
      {!loading && !err && filtered.length === 0 && (
        <EmptyState
          hasQuery={!!q || view !== "all"}
          reset={() => {
            setQ("");
            setView("all");
            setSort("order");
          }}
        />
      )}

      {/* ================= Grid ================= */}
      {!loading && !err && filtered.length > 0 && (
        <>
          <ul
            ref={gridRef}
            className="puppies-grid"
            role="list"
            aria-label="Puppies and litters"
          >
            {visible.map((item, idx) => (
              <li key={item.id} id={item.id || `entry-${idx}`} className="pp-cell">
                {item.entryType === "puppy" ? (
                  <PuppyCard item={item} index={idx} />
                ) : (
                  <LitterCard item={item} index={idx} />
                )}
              </li>
            ))}
          </ul>

          {/* Pagination */}
          <Pagination
            page={page}
            totalPages={pages.length}
            canPrev={page > 0}
            canNext={page < pages.length - 1}
            onPrev={() => {
              if (page > 0) setPage((p) => p - 1);
              gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            onNext={() => {
              if (page < pages.length - 1) setPage((p) => p + 1);
              gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            onLoadMore={() => {
              if (page < pages.length - 1) setPage((p) => p + 1);
            }}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------
// Controls Bar
// ---------------------------------------------

function ControlsBar({ view, setView, q, setQ, sort, setSort, resultsCount }) {
  return (
    <section className="pp-controls" role="region" aria-label="Filters and search">
      {/* Segmented filter */}
      <div className="seg" role="tablist" aria-label="Type filter">
        <button
          role="tab"
          aria-selected={view === "all"}
          className={`seg-btn ${view === "all" ? "is-active" : ""}`}
          onClick={() => setView("all")}
        >
          All
        </button>
        <button
          role="tab"
          aria-selected={view === "puppy"}
          className={`seg-btn ${view === "puppy" ? "is-active" : ""}`}
          onClick={() => setView("puppy")}
        >
          Puppies
        </button>
        <button
          role="tab"
          aria-selected={view === "litter"}
          className={`seg-btn ${view === "litter" ? "is-active" : ""}`}
          onClick={() => setView("litter")}
        >
          Litters
        </button>
      </div>

      {/* Search */}
      <div className="pp-search" style={{ flex: 1 }}>
        <label className="sr-only" htmlFor="puppy-search">
          Search
        </label>
        <input
          id="puppy-search"
          type="search"
          placeholder="Search by name, parent, color, status"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search puppies and litters"
        />
      </div>

      {/* Sort */}
      <div className="pp-sort">
        <label htmlFor="puppy-sort" style={{ display: "block" }}>
          <span className="sr-only">Sort</span>
        </label>
        <select
          id="puppy-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort results"
        >
          <option value="order">Featured</option>
          <option value="newest">Newest</option>
          <option value="price">Price</option>
          <option value="name">Name</option>
        </select>
      </div>

      {/* Count */}
      <div className="pp-count" aria-live="polite">
        {resultsCount} result{resultsCount === 1 ? "" : "s"}
      </div>
    </section>
  );
}

// ---------------------------------------------
// Cards
// ---------------------------------------------

function PuppyCard({ item, index }) {
  const [ref, seen] = useIntersection();
  const price = money(item.price);
  const labels = {
    color: item.color || "",
    sex: item.sex || "",
    dob: safeDate(item.dob),
    available: safeDate(item.availableDate),
  };
  const status = String(item.status || "Available").toLowerCase();

  return (
    <article ref={ref} className={`puppy-card ${seen ? "is-in" : ""}`} aria-label={`${item.name} puppy`}>
      <figure className="puppy-img-wrap">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} className="puppy-img" loading="lazy" />
        ) : (
          <div className="puppy-img placeholder" role="img" aria-label="No photo yet">
            Photo coming soon
          </div>
        )}

        <div className="pp-badges">
          <span className={`badge-type ${item.entryType}`}>
          {item.entryType === "puppy" ? "Puppy" : "Litter"}
        </span>

          <span className={`status status-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
        </div>
      </figure>

      <div className="puppy-content">
        <div className="puppy-heading">
          <h3 className="puppy-name" title={item.name}>
            {item.name}
          </h3>
          {price ? <span className="puppy-price">{price}</span> : <span className="puppy-price muted">Contact for pricing</span>}
        </div>

        {item.description && <p className="puppy-description">{item.description}</p>}

        <dl className="puppy-meta">
          {labels.color && (<><dt>Color</dt><dd>{labels.color}</dd></>)}
          {labels.sex && (<><dt>Sex</dt><dd>{labels.sex}</dd></>)}
          {labels.dob && (<><dt>Date of Birth</dt><dd>{labels.dob}</dd></>)}
          {labels.available && (<><dt>Available</dt><dd>{labels.available}</dd></>)}
          {item.damName && (<><dt>Dam</dt><dd>{item.damName}</dd></>)}
          {item.studName && (<><dt>Stud</dt><dd>{item.studName}</dd></>)}
        </dl>
      </div>

      <footer className="puppy-footer">
        <Link to="/contact" className="puppy-cta" aria-label={`Inquire about ${item.name}`}>
          Inquire
        </Link>
      </footer>
    </article>
  );
}

function LitterCard({ item, index }) {
  const [ref, seen] = useIntersection();
  const price = money(item.price);
  const labels = {
    expected: safeDate(item.expectedDate),
    expectedColor: item.color || "", // reusing `color` for expected colors
  };

  return (
    <article ref={ref} className={`puppy-card pp-card-litter ${seen ? "is-in" : ""}`} aria-label={`${item.name} upcoming litter`}>
      <figure className="puppy-img-wrap">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={`${item.name} litter`} className="puppy-img" loading="lazy" />
        ) : (
          <div className="puppy-img placeholder" role="img" aria-label="Litter photo coming soon">
            Upcoming litter
          </div>
        )}

        <div className="pp-badges">
          <span className={`badge-type ${item.entryType}`}>
            {item.entryType === "puppy" ? "Puppy" : "Litter"}
          </span>

  
        </div>
      </figure>
      <div className="puppy-content">

        
        <div className="puppy-heading">
          <h3 className="puppy-name" title={item.name}>
            {item.name}
          </h3>
          {price ? <span className="puppy-price">{price}</span> : <span className="puppy-price muted">Contact for pricing</span>}
        </div>

        {item.description && <p className="puppy-description">{item.description}</p>}

        <dl className="puppy-meta">
          {labels.expected && (<><dt>Expected</dt><dd>{labels.expected}</dd></>)}
          {labels.expectedColor && (<><dt>Expected Color</dt><dd>{labels.expectedColor}</dd></>)}
          {item.damName && (<><dt>Dam</dt><dd>{item.damName}</dd></>)}
          {item.studName && (<><dt>Stud</dt><dd>{item.studName}</dd></>)}
        </dl>
      </div>

      <footer className="puppy-footer">
        <Link to="/contact" className="puppy-cta" aria-label={`Join waitlist for ${item.name} litter`}>
          Join Waitlist
        </Link>
      </footer>
    </article>
  );
}

function PuppyDescription({ text, limit = 140 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const isLong = text.length > limit;
  const shown = expanded ? text : text.slice(0, limit) + (isLong ? "…" : "");

  return (
    <p className="puppy-description">
      {shown}
      {isLong && (
        <button
          type="button"
          className="desc-toggle"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? " Show less" : " Read more"}
        </button>
      )}
    </p>
  );
}

// ---------------------------------------------
// Pagination
// ---------------------------------------------

function Pagination({ page, totalPages, canPrev, canNext, onPrev, onNext, onLoadMore }) {
  if (totalPages <= 1) return null;

  return (
    <nav className="pp-pager" aria-label="Pagination">
      <button className="pp-pagebtn" onClick={onPrev} disabled={!canPrev} aria-disabled={!canPrev}>
        ← Previous
      </button>

      <span className="pp-pagecount" aria-live="polite">
        Page {page + 1} of {totalPages}
      </span>

      <button className="pp-pagebtn" onClick={onNext} disabled={!canNext} aria-disabled={!canNext}>
        Next →
      </button>

      {/* Progressive reveal for mobile: quick load more */}
      {canNext && (
        <button className="pp-loadmore" onClick={onLoadMore}>
          Load more
        </button>
      )}
    </nav>
  );
}

// ---------------------------------------------
// Skeletons & Empty state
// ---------------------------------------------

function LoaderSkeleton({ count = 8 }) {
  return (
    <ul className="puppies-grid" role="list" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="pp-cell">
          <div className="puppy-card skel">
            <div className="puppy-img-wrap" />
            <div className="puppy-content">
              <div className="skel-line w60" />
              <div className="skel-line w90" />
              <div className="skel-line w70" />
              <div className="skel-line w40" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ hasQuery, reset }) {
  return (
    <section className="pp-empty" aria-labelledby="pp-empty-title">
      <div className="pp-empty-illus" aria-hidden="true">
        <div className="dot" />
        <div className="dot" />
        <div className="dot" />
      </div>
      <h3 id="pp-empty-title">{hasQuery ? "No matches found" : "Nothing here yet"}</h3>
      <p>
        {hasQuery
          ? "Try a different search or filter. "
          : "Please check back soon for new puppies and upcoming litters. "}
        You can also{" "}
        <Link to="/contact" className="pp-cta alt">
          contact us
        </Link>{" "}
        to join the waitlist.
      </p>
      {hasQuery && (
        <button className="pp-cta alt" onClick={reset}>
          Reset filters
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------
// SR-only helper class (in case CSS doesn’t include one yet)
// ---------------------------------------------
/* eslint-disable */
const srOnlyStyle = document.createElement("style");
srOnlyStyle.innerHTML = `
.sr-only {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0);
  white-space: nowrap; border: 0;
}`;
if (typeof document !== "undefined") {
  document.head.appendChild(srOnlyStyle);
}
/* eslint-enable */
